<script setup>
import moment from "moment";
import { computed, ref, watch } from "vue";
import { PageSidebar } from "@easy/ui/page-components";
import {
    FormFiles,
    FormInput,
    FormInputColor,
    FormInputDate,
    FormLabel,
    FormRichText,
    FormSelect,
    FormSwitch,
    FormToggleSm,
    makeFileModel,
} from "@easy/ui/forms";
import { PageIcon, TrophyIcon } from "@easy/ui/icons";
import { NotificationInfo } from "@easy/ui/notifications";
import cSaveButton from "@/shared/components/cSaveButton.vue";
import { certificateTemplatesService } from "@/apps/player/services/certificate-templates.service";
import { PLAYER_PREVIEW_KIND, PLAYER_ROUTE_NAMES } from "@/apps/player/player.routes";

import { useActions, useState, useGetters } from "@/store/vuex-composition-mappers";
import { useFormValidation } from "@/shared/utils/forms/useFormValidation";
import { maxValue, minDateValue, minValue, required } from "@/shared/utils/forms/i18n-vuelidators";
import { useRoute, useRouter } from "vue-router";

const { lang } = useState(["lang"]);
const { hasChanges, certificate } = useState("player", ["hasChanges", "certificate"]);
const { hasCartProductConnected } = useGetters("player", ["hasCartProductConnected"]);
const { saveSnapshot } = useActions("player/projects", ["saveSnapshot"]);
const { uploadImage } = useActions("player", ["uploadImage"]);

const route = useRoute();
const router = useRouter();

const tomorrow = moment().add(1, "d").startOf("day");
const { v$, validateForm } = useFormValidation(
    {
        progressThreshold: { minValue: minValue(0), maxValue: maxValue(100) },
        issueImpossibleBefore: { minValue: minDateValue(tomorrow) },
        templateName: { required },
    },
    certificate
);
async function handleFormSave() {
    if (await validateForm()) {
        saveSnapshot();
    }
}

const certificateTemplate = computed(() =>
    certificateTemplatesService.getPlayerCertTemplate(certificate.value.templateName)
);

const viewType = computed({
    get: () => route.query.view,
    set: (value) => {
        router.push({
            query: { ...route.query, view: value },
        });
    },
});

// <== Course Image Upload

const files = ref([]);
const filesBack = ref([]);

async function uploadCourseImage($event) {
    certificate.value.content.courseImage = await uploadImage({ file: $event });
}
function removeCourseImage() {
    certificate.value.content.courseImage = null;
}
async function uploadCourseBackground($event) {
    certificate.value.content.backgroundImage = await uploadImage({ file: $event });
}
function removeCourseBackground() {
    certificate.value.content.backgroundImage = null;
}
watch(
    certificate,
    () => {
        if (certificate.value.content?.courseImage) {
            files.value = [makeFileModel({ url: certificate.value.content.courseImage })];
        }
        if (certificate.value.content?.backgroundImage) {
            filesBack.value = [makeFileModel({ url: certificate.value.content.backgroundImage })];
        }
    },
    { immediate: true }
);

//  Course Image Upload ==>
</script>

<template>
    <PageSidebar>
        <template #sidebarTitle> {{ $t("player.menu.certificates") }} </template>
        <template #sidebarMain>
            <div class="p-6 space-y-5">
                <NotificationInfo :closeButton="false" v-if="!hasCartProductConnected">
                    {{ $t("player.certificates.product_info") }}
                    <router-link
                        class="underline"
                        :to="{ name: PLAYER_ROUTE_NAMES.projectPaywall }"
                    >
                        {{ $t("player.certificates.product_info_link") }}
                    </router-link>
                </NotificationInfo>
                <FormToggleSm
                    v-model="certificate.isActive"
                    :title="$t('player.certificates.is_active')"
                />
                <FormInput
                    v-model.number="certificate.progressThreshold"
                    @blur="v$.progressThreshold.$touch"
                    :error="v$.progressThreshold.$errors?.[0]?.$message"
                    :title="$t('player.certificates.progress_threshold')"
                />
                <FormInputDate
                    :title="$t('player.certificates.issue_impossible_before')"
                    :lang="lang"
                    @blur="v$.issueImpossibleBefore.$touch"
                    :error="v$.issueImpossibleBefore.$errors?.[0]?.$message"
                    v-model="certificate.issueImpossibleBefore"
                    canReset
                />

                <div class="space-y-2">
                    <FormLabel>{{ $t("player.certificates.switch_preview") }}</FormLabel>
                    <FormSwitch
                        v-model="viewType"
                        :options="[
                            {
                                value: PLAYER_PREVIEW_KIND.certificate,
                                label: $t('player.views.certificate'),
                                icon: PageIcon,
                            },
                            {
                                value: PLAYER_PREVIEW_KIND.badge,
                                label: $t('player.views.badge'),
                                icon: TrophyIcon,
                            },
                        ]"
                    />
                </div>

                <FormSelect
                    :value="certificate.templateName"
                    v-model="certificate.templateName"
                    :label="$t('player.certificates.template')"
                    :options="certificateTemplatesService.getAllValues()"
                    @blur="v$.templateName.$touch"
                    :error="v$.templateName.$errors?.[0]?.$message"
                />
                <template v-if="certificate.content && certificateTemplate?.fields">
                    <FormInput
                        v-if="certificateTemplate.fields.includes('courseName')"
                        v-model="certificate.content.courseName"
                        :title="$t('player.certificates.course_name')"
                    />
                    <FormInputColor
                        v-if="certificateTemplate.fields.includes('color')"
                        :title="$t('player.certificates.certificate_color')"
                        v-model="certificate.content.color"
                        :tooltip="$t('shared.select_color_info')"
                    />
                    <FormRichText
                        v-if="certificateTemplate.fields.includes('courseDescription')"
                        v-model="certificate.content.courseDescription"
                        :title="$t('player.certificates.course_description')"
                    />
                    <FormFiles
                        v-if="certificateTemplate.fields.includes('courseImage')"
                        :title="$t('player.certificates.course_image')"
                        :labels="{
                            clickToUpload: $t('shared.uploader.click_to_upload'),
                            dragAndDrop: $t('shared.uploader.drag_and_drop'),
                            fileInfo: $t('shared.uploader.file_details'),
                        }"
                        :maxSize="10000"
                        :errorMessages="{
                            invalidSize: $t('shared.invalid.file_size_over'),
                            invalidType: $t('shared.invalid.file_type'),
                        }"
                        :single-file="true"
                        v-model="files"
                        @fileSelected="uploadCourseImage"
                        @fileRemoved="removeCourseImage"
                    />
                    <FormFiles
                        v-if="certificateTemplate.fields.includes('backgroundImage')"
                        :title="$t('player.certificates.certificate_background')"
                        :labels="{
                            clickToUpload: $t('shared.uploader.click_to_upload'),
                            dragAndDrop: $t('shared.uploader.drag_and_drop'),
                            fileInfo: $t('shared.uploader.file_details'),
                        }"
                        :maxSize="10000"
                        :errorMessages="{
                            invalidSize: $t('shared.invalid.file_size_over'),
                            invalidType: $t('shared.invalid.file_type'),
                        }"
                        :single-file="true"
                        v-model="filesBack"
                        @fileSelected="uploadCourseBackground"
                        @fileRemoved="removeCourseBackground"
                    />
                    <FormInputColor
                        v-if="certificateTemplate.fields.includes('textColor')"
                        :title="$t('player.certificates.certificate_text_color')"
                        v-model="certificate.content.textColor"
                        :tooltip="$t('shared.select_color_info')"
                    />
                </template>
            </div>
        </template>
        <template #sidebarFooter>
            <cSaveButton @save="handleFormSave" :disabled="!hasChanges" />
        </template>
    </PageSidebar>
</template>
